import { useEffect } from 'react';
import { fetchMyProfile } from '../../data/accounts.ts';
import { supabase } from '../../data/supabase.ts';
import { purgeOrderLocalData } from '../../local/cleanup.ts';
import { listDrafts } from '../../local/drafts.ts';
import { listQueue } from '../../media/queue.ts';
import { processQueue } from '../../media/uploader.ts';

const RETRY_MS = 60_000;

/**
 * Retoma las cargas pendientes al abrir la app, al recuperar la conexión y periódicamente con la
 * app visible. Al iniciar, descarta datos locales de órdenes que ya no son accesibles (C12).
 * No hay ejecución en segundo plano con la app cerrada.
 */
export function QueueRunner({ userId }: { userId: string }) {
  useEffect(() => {
    let cancelled = false;
    const run = () => {
      if (!cancelled && document.visibilityState === 'visible' && navigator.onLine)
        void processQueue(userId).catch(() => undefined);
    };

    void sweepInaccessible(userId).finally(run);
    const timer = setInterval(run, RETRY_MS);
    window.addEventListener('online', run);
    document.addEventListener('visibilitychange', run);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener('online', run);
      document.removeEventListener('visibilitychange', run);
    };
  }, [userId]);
  return null;
}

async function sweepInaccessible(userId: string): Promise<void> {
  try {
    const [drafts, queue] = await Promise.all([listDrafts(userId), listQueue(userId)]);
    const ids = [...new Set([...drafts.map((d) => d.orderId), ...queue.map((q) => q.orderId)])];
    if (ids.length === 0 || !navigator.onLine) return;
    const { data, error } = await supabase.from('orders').select('id').in('id', ids);
    if (error) return; // ante cualquier error no se borra nada
    await fetchMyProfile(); // confirma que la sesión y la cuenta siguen vigentes
    const visible = new Set(data.map((o) => o.id));
    await Promise.all(ids.filter((id) => !visible.has(id)).map((id) => purgeOrderLocalData(userId, id)));
  } catch {
    // sin conexión o IndexedDB no disponible: se reintenta en el próximo inicio
  }
}
